import { Ship } from 'lucide-react';
import { useEffect, useRef } from 'react';

const GenerateIcons = () => {
    // Only run in development
    if (import.meta.env.PROD) {
        return null;
    }

    const faviconRef = useRef<HTMLCanvasElement>(null);
    const appleIconRef = useRef<HTMLCanvasElement>(null);
    const ogImageRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const generateIcon = async (canvas: HTMLCanvasElement | null, size: number) => {
            if (!canvas) {
                throw new Error('Canvas reference is null');
            }
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                throw new Error('Could not get canvas context');
            }

            console.log(`Generating ${size}x${size} icon...`);

            // Set canvas size
            canvas.width = size;
            canvas.height = size;

            // Draw background - using the sunset/yellow color from your theme
            ctx.fillStyle = '#FF9F1C';
            ctx.fillRect(0, 0, size, size);

            // Create SVG for ship icon - using the navy/dark blue color from your theme
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size * 0.6}" height="${size * 0.6}" viewBox="0 0 24 24" fill="none" stroke="#0A192F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/><path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/><path d="M12 9v4"/><path d="M12 2v3"/></svg>`;

            // Create image from SVG
            const img = new Image();
            img.src = 'data:image/svg+xml;base64,' + btoa(svg);

            await new Promise((resolve, reject) => {
                img.onload = () => {
                    try {
                        // Center the icon
                        ctx.drawImage(
                            img,
                            (size - size * 0.6) / 2,
                            (size - size * 0.6) / 2,
                            size * 0.6,
                            size * 0.6
                        );
                        resolve(null);
                    } catch (error) {
                        reject(error);
                    }
                };
                img.onerror = () => reject(new Error('Failed to load image'));
            });

            console.log(`Generated ${size}x${size} icon successfully`);
            return canvas.toDataURL('image/png').split(',')[1];
        };

        const saveBase64AsFile = async (base64: string, filename: string) => {
            console.log(`Saving ${filename}...`);
            const response = await fetch(`/api/save-icon`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    base64,
                    filename,
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(`Failed to save ${filename}: ${error.error}`);
            }

            const result = await response.json();
            console.log(`Saved ${filename} successfully at ${result.path}`);
        };

        const generateAllIcons = async () => {
            try {
                console.log('Starting icon generation...');

                // Generate all icons - using the same colors for all sizes
                const favicon = await generateIcon(faviconRef.current, 32);
                const appleIcon = await generateIcon(appleIconRef.current, 180);
                const ogImage = await generateIcon(ogImageRef.current, 1200);

                // Save all icons
                await Promise.all([
                    favicon && saveBase64AsFile(favicon, 'favicon.png'),
                    appleIcon && saveBase64AsFile(appleIcon, 'apple-touch-icon.png'),
                    ogImage && saveBase64AsFile(ogImage, 'og-image.png')
                ]);

                console.log('All icons generated and saved successfully!');
            } catch (error) {
                console.error('Error in icon generation:', error);
            }
        };

        generateAllIcons();
    }, []);

    return (
        <div style={{ display: 'none' }}>
            <canvas ref={faviconRef} />
            <canvas ref={appleIconRef} />
            <canvas ref={ogImageRef} />
        </div>
    );
};

export default GenerateIcons;